<template>
  <div class="sp-layout">
    <div class="sp-panel-wrap">
      <div class="sp-panel">
        <!-- 机组从未上报设定值（变量映射缺失/未配置）：明确提示而不是按钮永久置灰 -->
        <el-alert v-if="spDataMissing" type="info" :closable="false" class="deadband-alert"
          :title="t('message.rtu.noSpData')" />
        <div v-for="f in numericFields" :key="f.key" class="sp-row">
          <span class="name">
            {{ t(`message.rtu.${f.key}`) }}
            <span v-if="isDirty(f.var)" class="modflag">●</span>
          </span>
          <span class="stepper">
            <el-button size="small" :disabled="!liveSeen[f.var]" :aria-label="`decrease ${f.key}`" @click="step(f.var, -0.5)">−</el-button>
            <span class="num">{{ liveSeen[f.var] ? Number(draft[f.var]).toFixed(1) : '—' }}</span>
            <el-button size="small" :disabled="!liveSeen[f.var]" :aria-label="`increase ${f.key}`" @click="step(f.var, +0.5)">+</el-button>
            <!-- 分组且有偏置时：显示本机实际写入值（基准+偏置） -->
            <span v-if="ownOff(f.var)" class="eff" :title="t('message.rtu.effTip')">
              → {{ (Number(draft[f.var]) + myOffset).toFixed(1) }}°
            </span>
          </span>
        </div>

        <!-- 在室状态无覆盖功能：CT=3（区域传感器模式）下固件只按本机时间表运行
             （Changlin 2026-07-18）。调整在室时间请使用时间表编辑器。 -->
        <div class="sp-row">
          <span class="name">{{ t('message.rtu.fanMode') }} <span v-if="isDirty('fan_mode')" class="modflag">●</span></span>
          <el-radio-group v-model="draft.fan_mode" size="small" :disabled="!liveSeen['fan_mode']">
            <el-radio-button :value="0">{{ t('message.rtu.auto') }}</el-radio-button>
            <el-radio-button :value="1">{{ t('message.rtu.fanOnOpt') }}</el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="groupPeers.length" class="group-note">
          <el-icon><Share /></el-icon>
          <span>
            {{ t('message.rtu.groupMember', { g: myGroup }) }}
            {{ t('message.rtu.groupPropagates') }}
            <b>{{ t('message.rtu.groupSelf') }} ({{ myOffset >= 0 ? '+' : '' }}{{ myOffset }}°)</b><template v-for="p in groupPeers" :key="p.unitId">, <b>{{ p.name }} ({{ p.offset >= 0 ? '+' : '' }}{{ p.offset }}°)</b></template>
          </span>
        </div>

        <el-alert v-if="deadbandError" type="warning" :closable="false" class="deadband-alert"
          :title="t('message.rtu.deadbandError', { n: DEADBAND })" />

        <div class="apply-row">
          <el-button type="primary" :disabled="!dirtyVars.length || !!deadbandError" :loading="writing" @click="apply">
            {{ writing ? t('message.rtu.applying') : t('message.rtu.apply') }}
          </el-button>
          <el-button :disabled="!dirtyVars.length || writing" @click="discard">{{ t('message.rtu.discard') }}</el-button>
          <span v-if="confirming" class="confirming"><el-icon class="is-loading"><Loading /></el-icon> {{ t('message.rtu.confirmingNote') }}</span>
          <span v-else-if="confirmedAt" class="confirmed">✓ {{ t('message.rtu.applied') }} {{ confirmedAt }}</span>
        </div>
        <p class="microcopy">{{ t('message.rtu.spStagedNote') }}</p>
      </div>
    </div>

    <div class="context-col">
      <div class="tiles">
        <div class="tile">
          <div class="lbl">{{ t('message.rtu.roomTemp') }}</div>
          <div class="val">{{ fmt(values.room_temp, 1) }}<small>°F</small></div>
        </div>
        <div class="tile">
          <div class="lbl">{{ t('message.rtu.modeNow') }}</div>
          <div class="val mode">{{ values.occupied ? t('message.rtu.occupiedOpt') : t('message.rtu.unoccOpt') }}</div>
        </div>
        <div class="tile">
          <div class="lbl">{{ t('message.rtu.activeSetpoint') }}</div>
          <div class="val">{{ fmt(activeSetpoint, 1) }}<small>°F</small></div>
        </div>
      </div>
      <p class="deadband-note">{{ t('message.rtu.deadbandNote', { n: DEADBAND }) }}</p>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus';
import { Share, Loading } from '@element-plus/icons-vue';
import { getGroupConfig, computeGroupWrites, applyOwnOffset, GROUP_SP_VARS } from '@/services/rtuGroups';

const props = defineProps({
  adapter: { type: Object, required: true },
  unitId: { type: String, required: true },
  values: { type: Object, required: true },
});
const { t } = useI18n();

// 分组联动：面板中输入的是“组基准值”；应用时本机与同组各成员均写入 基准+各自偏置
const groupCfg = ref({});
const myGroup = ref(0);
const myOffset = ref(0);
const groupPeers = ref([]); // [{unitId, name, offset}]

// 本机在分组中的温度设定偏置（非分组或非温度设定值为 0）
const ownOff = (name) => (myGroup.value && GROUP_SP_VARS.includes(name) ? myOffset.value : 0);

onMounted(async () => {
  try {
    groupCfg.value = await getGroupConfig();
    const me = groupCfg.value[props.unitId];
    myGroup.value = me?.group || 0;
    myOffset.value = me?.offset || 0;
    if (myGroup.value) {
      const units = await props.adapter.getUnits();
      const nameOf = (id) => units.find((u) => u.unitId === id)?.name || id;
      groupPeers.value = Object.entries(groupCfg.value)
        .filter(([id, c]) => id !== props.unitId && c.group === myGroup.value)
        .map(([id, c]) => ({ unitId: id, name: nameOf(id), offset: c.offset }));
      syncFromLive(); // 偏置已知后，把已加载的实际值换算回基准值
    }
  } catch (e) {
    console.warn('[rtu] group config load failed:', e?.message);
  }
});

const DEADBAND = 2; // °F：制热/制冷设定值最小间隔

const SP_VARS = ['sp_occ_heat', 'sp_occ_cool', 'sp_unocc_heat', 'sp_unocc_cool', 'fan_mode'];
const numericFields = [
  { key: 'occHeat', var: 'sp_occ_heat' },
  { key: 'occCool', var: 'sp_occ_cool' },
  { key: 'unoccHeat', var: 'sp_unocc_heat' },
  { key: 'unoccCool', var: 'sp_unocc_cool' },
];

const draft = reactive({});
const confirmed = reactive({});
// 机组实际上报过的设定值键：未上报的不可编辑、不参与死区校验、不下发
//（否则草稿值全 0 → 死区校验永远不通过，Apply 永久置灰——张之伦 2026-07-20）
const liveSeen = reactive({});
const writing = ref(false);
const confirming = ref(false); // 下发后后台主动点名确认中
const confirmedAt = ref('');

const isDirtyKey = (k) => k in confirmed && draft[k] !== confirmed[k];
const isDirty = (k) => isDirtyKey(k);
const dirtyVars = computed(() => SP_VARS.filter((k) => liveSeen[k] && isDirtyKey(k)));

function syncFromLive() {
  SP_VARS.forEach((k) => {
    const live = props.values[k];
    if (live !== undefined && live !== null) {
      liveSeen[k] = true;
      if (!isDirtyKey(k)) {
        // 分组时步进器表示“组基准值”＝设备实际值 − 本机偏置
        draft[k] = Number(live) - ownOff(k);
        confirmed[k] = Number(live) - ownOff(k);
      }
    }
  });
}

// 初始 + 实时数据到达时同步（不覆盖已修改项）
SP_VARS.forEach((k) => { draft[k] = Number(props.values[k] ?? 0); confirmed[k] = Number(props.values[k] ?? 0); });
syncFromLive();
watch(() => [props.values.sp_occ_heat, props.values.sp_unocc_cool, props.values.fan_mode], syncFromLive);

const step = (k, d) => { draft[k] = Math.round((Number(draft[k]) + d) * 2) / 2; };
const fmt = (v, dp) => (v == null ? '—' : Number(v).toFixed(dp));

// 有实时数据但 4 个温度设定值一个都没上报过 → 变量映射缺失，明确提示
const spDataMissing = computed(() =>
  Object.keys(props.values).length > 0 && numericFields.every((f) => !liveSeen[f.var])
);

// 死区校验只对机组实际上报过的一对设定值生效
const pairBlocked = (h, c) => liveSeen[h] && liveSeen[c] && draft[c] - draft[h] < DEADBAND;
const deadbandError = computed(() =>
  pairBlocked('sp_occ_heat', 'sp_occ_cool') || pairBlocked('sp_unocc_heat', 'sp_unocc_cool')
);

const activeSetpoint = computed(() =>
  props.values.occupied ? props.values.sp_occ_heat : props.values.sp_unocc_heat
);

function discard() {
  SP_VARS.forEach((k) => { draft[k] = confirmed[k]; });
  confirmedAt.value = '';
}

async function apply() {
  // writes 为基准值；本机实际写入 = 基准 + 本机偏置
  const writes = dirtyVars.value.map((name) => ({ name, value: draft[name] }));
  const selfWrites = applyOwnOffset(groupCfg.value, props.unitId, writes);
  writing.value = true;
  try {
    const results = await props.adapter.writeVars(props.unitId, selfWrites);
    results.forEach((r) => {
      if (r.success) {
        // confirmed=读回实际值；未回报时为已下发值（乐观更新，推送到达后自动校正）
        confirmed[r.name] = Number(r.value) - ownOff(r.name);
        draft[r.name] = Number(r.value) - ownOff(r.name);
      }
    });
    const sendFailed = results.filter((r) => r.reason === 'send_failed');
    const unmapped = results.filter((r) => r.reason === 'unmapped');
    const pending = results.filter((r) => r.success && !r.confirmed);
    if (sendFailed.length) {
      ElMessage.error(t('message.rtu.writeFailed') + ': ' + sendFailed.map((f) => f.name).join(', '));
    } else if (unmapped.length) {
      // 未映射：不是下发失败，而是该机组模板未配置这些寄存器——引导查映射
      ElMessage.warning(t('message.rtu.writeUnmapped', { vars: unmapped.map((f) => f.name).join(', ') }));
    } else if (pending.length) {
      // 真实网关按自身周期回读寄存器，几分钟内回显都算正常——不是失败。
      // 下发已含读请求；再主动点名并延长确认窗口，回读到即转“已确认”。
      confirmedAt.value = '';
      ElMessage.info(t('message.rtu.writeConfirming'));
      confirmAfterWrite(selfWrites); // 后台确认，不阻塞
    } else {
      confirmedAt.value = new Date().toLocaleTimeString();
      ElMessage.success(t('message.rtu.spApplied'));
    }
    // 分组联动：把温度设定值 + 各成员偏置写给组内其它 RTU
    if (myGroup.value) {
      const groupWrites = computeGroupWrites(groupCfg.value, props.unitId, writes);
      if (groupWrites.length) {
        const peerResults = await Promise.all(
          groupWrites.map((g) => props.adapter.sendVars(g.unitId, g.writes).catch(() => ({ success: false })))
        );
        const ok = peerResults.filter((r) => r.success).length;
        if (ok) ElMessage.success(t('message.rtu.groupApplied', { n: ok }));
      }
    }
  } catch (e) {
    console.error('[rtu] setpoint write failed:', e);
    ElMessage.error(t('message.rtu.writeFailed'));
  } finally {
    writing.value = false;
  }
}

// 下发后主动点名确认：真实网关按自身周期回读，读请求回显需时间。
// 多次 refresh()（点名读）延长确认窗口，回读到写入值即转“已确认”；
// 超时不回读也不报失败（仍属正常，值稍后自然推送）。
async function confirmAfterWrite(writes) {
  if (!props.adapter.refresh || confirming.value) return;
  confirming.value = true;
  try {
    const delays = [5000, 12000, 15000];
    for (const d of delays) {
      await new Promise((r) => setTimeout(r, d));
      let snap;
      try { snap = await props.adapter.refresh(props.unitId); } catch (e) { continue; }
      const allMatch = writes.every(({ name, value }) => {
        const actual = snap.values[name];
        return actual != null && Math.abs(Number(actual) - Number(value)) < 0.11;
      });
      if (allMatch) {
        writes.forEach(({ name, value }) => {
          confirmed[name] = Number(value) - ownOff(name);
          if (!isDirtyKey(name)) draft[name] = Number(value) - ownOff(name);
        });
        confirmedAt.value = new Date().toLocaleTimeString();
        ElMessage.success(t('message.rtu.spConfirmed'));
        return;
      }
    }
  } finally {
    confirming.value = false;
  }
}
</script>

<style scoped>
.sp-layout { display: grid; grid-template-columns: 380px minmax(0, 1fr); gap: 20px; align-items: start; }
@media (max-width: 900px) { .sp-layout { grid-template-columns: 1fr; } }
.sp-panel-wrap { border-right: 1px solid var(--el-border-color-lighter); padding-right: 20px; }
@media (max-width: 900px) { .sp-panel-wrap { border-right: 0; padding-right: 0; } }
.sp-panel { display: flex; flex-direction: column; gap: 14px; }
.sp-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.sp-row .name { font-size: 13px; color: var(--el-text-color-regular); }
.modflag { color: var(--el-color-primary); font-weight: 700; }
.stepper { display: inline-flex; align-items: center; gap: 0; }
.stepper .num { width: 62px; text-align: center; font-variant-numeric: tabular-nums; font-weight: 600; font-size: 14.5px; }
.stepper .eff { margin-left: 8px; font-size: 12px; color: var(--el-color-primary); font-variant-numeric: tabular-nums; white-space: nowrap; }
.apply-row { display: flex; align-items: center; gap: 10px; margin-top: 4px; flex-wrap: wrap; }
.confirmed { font-size: 12px; color: var(--el-color-success); }
.confirming { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: var(--el-text-color-secondary); }
.microcopy { font-size: 11.5px; color: var(--el-text-color-secondary); margin: 0; }
.deadband-alert { --el-alert-padding: 6px 10px; }
.group-note {
  display: flex; align-items: flex-start; gap: 6px; font-size: 12px;
  color: var(--el-text-color-secondary); background: var(--el-color-primary-light-9);
  border-radius: 4px; padding: 7px 10px; line-height: 1.5;
}
.group-note b { color: var(--el-text-color-regular); font-weight: 600; }

.context-col { display: flex; flex-direction: column; gap: 10px; }
.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; }
.tile { background: var(--el-fill-color-light); border: 1px solid var(--el-border-color-lighter); border-radius: 6px; padding: 10px 12px; }
.tile .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--el-text-color-secondary); }
.tile .val { font-size: 22px; font-weight: 600; font-variant-numeric: tabular-nums; margin-top: 2px; }
.tile .val.mode { font-size: 16px; }
.tile .val small { font-size: 12px; font-weight: 400; color: var(--el-text-color-secondary); margin-left: 2px; }
.deadband-note { font-size: 12px; color: var(--el-text-color-secondary); border-left: 3px solid var(--el-border-color); padding-left: 10px; margin: 0; }
</style>
